import { Box, Flex, Icon, Tooltip } from "@chakra-ui/react";
import { IconType } from "react-icons";
import {
  FaCalculator,
  FaClipboardCheck,
  FaCrosshairs,
  FaFileContract,
  FaFingerprint,
  FaHandshake,
  FaIdCard,
  FaMapMarkerAlt,
  FaPenNib,
  FaPiggyBank,
  FaSearch,
  FaShieldAlt,
  FaUser,
  FaWhatsapp,
} from "react-icons/fa";
import {
  AuctionProposalStatus,
  NegotiationSubStep,
} from "@/models/credit-auction-proposal.model";
import {
  NEGOTIATION_SUB_STEP_LABELS,
  NEGOTIATION_SUB_STEP_ORDER,
} from "@/utils/auctionProposalStatus";

const SUB_STEP_ICONS: Record<NegotiationSubStep, IconType> = {
  [NegotiationSubStep.CPF_INCLUSION]: FaIdCard,
  [NegotiationSubStep.WHATSAPP_CONFIRMATION]: FaWhatsapp,
  [NegotiationSubStep.SIMULATION]: FaCalculator,
  [NegotiationSubStep.PERSONAL_DATA]: FaUser,
  [NegotiationSubStep.ADDRESS]: FaMapMarkerAlt,
  [NegotiationSubStep.BANK_DATA]: FaPiggyBank,
  [NegotiationSubStep.ANALISE]: FaSearch,
  [NegotiationSubStep.PROPOSTA]: FaHandshake,
  [NegotiationSubStep.REVISAO]: FaClipboardCheck,
  [NegotiationSubStep.CONSENTIMENTO]: FaShieldAlt,
  // Geolocalização (GPS) — distinto de ADDRESS (endereço cadastral) que
  // usa FaMapMarkerAlt. FaCrosshairs evoca a "mira" de coordenadas.
  [NegotiationSubStep.LOCALIZATION]: FaCrosshairs,
  [NegotiationSubStep.ACEITE_CONTRATACAO]: FaPenNib,
  [NegotiationSubStep.BIOMETRIA]: FaFingerprint,
  [NegotiationSubStep.FORMALIZATION]: FaFileContract,
};

type NegotiationPhase = "before" | "during" | "after" | "error-during";

const STATUS_NEGOTIATION_PHASE: Record<AuctionProposalStatus, NegotiationPhase> = {
  [AuctionProposalStatus.CREATED]: "before",
  [AuctionProposalStatus.IN_AUCTION]: "before",
  [AuctionProposalStatus.CHOSEN]: "before",
  [AuctionProposalStatus.PENDING_BANK_ANALYSIS]: "before",
  [AuctionProposalStatus.SELFSERVICE]: "before",
  [AuctionProposalStatus.REPROVED]: "before",
  // Reprovado no processamento: o funil travou no passo onde falhou (ex.:
  // Simulação) — marcamos esse passo em vermelho via error-during.
  [AuctionProposalStatus.REPROVED_IN_PROCESS]: "error-during",
  [AuctionProposalStatus.PENDING_CONFIRMATION]: "during",
  [AuctionProposalStatus.ERROR_CREATE_BIOMETRIC_VALIDATION]: "before",
  [AuctionProposalStatus.PENDING_FORMALIZATION]: "during",
  [AuctionProposalStatus.PENDING_REFORMALIZATION]: "after",
  // "Corrigir dados bancários": o banco recusou a conta DEPOIS da formalização,
  // ou seja, o funil de autocontratação já estava completo. O problema é
  // pós-funil — aparece na badge de status e no stepper macro da modal, não
  // como erro num passo do funil. Aqui o funil fica completo (after).
  [AuctionProposalStatus.PENDING_USER_REVIEW]: "after",
  [AuctionProposalStatus.PAYED]: "after",
  [AuctionProposalStatus.IN_CANCELLATION]: "after",
  [AuctionProposalStatus.CANCELED]: "after",
  [AuctionProposalStatus.CANCELED_BY_CLIENT]: "after",
  // Esteira processando a simulação: o tomador está, de fato, no passo Análise
  // do funil (esperando a esteira). Mostra o funil em andamento (during).
  [AuctionProposalStatus.PROCESSING_SIMULATION]: "during",
  // Aguardando o tomador escolher a oferta: ele está no passo Proposta — fica
  // verde escuro (atual) enquanto escolhe; vira sucesso quando escolhe (#13).
  [AuctionProposalStatus.AWAITING_BORROWER_CHOICE]: "during",
   [AuctionProposalStatus.AGUARDANDO_VALIDADE_LEILAO]:"after"
  
};

// Sub-step que fica em ERRO (vermelho) para cada status de erro/pendência.
// Mapeado por sub-step (não índice fixo) para sobreviver a mudanças de ordem.
const ERROR_SUB_STEP: Partial<Record<AuctionProposalStatus, NegotiationSubStep>> = {
  [AuctionProposalStatus.ERROR_CREATE_BIOMETRIC_VALIDATION]:
    NegotiationSubStep.WHATSAPP_CONFIRMATION,
  // Reprovação no processamento ocorre na ANÁLISE (após simular e preencher
  // todos os dados), não na Simulação. Ponto fixo — o substep derivado do
  // funil é não-confiável aqui (pode vir 'simulacao=aguardando' por resíduo).
  [AuctionProposalStatus.REPROVED_IN_PROCESS]: NegotiationSubStep.ANALISE,
};

// Substep "atual" (verde escuro) fixo para macro-status que correspondem a um
// passo determinístico do funil. Evita depender da derivação do status_customizado,
// que pode oscilar por resíduo (ex.: 'simulacao=aguardando' em proposta antiga)
// ou pelo timing de marcação. PROCESSING_SIMULATION ≙ Análise (esteira rodando);
// AWAITING_BORROWER_CHOICE ≙ Proposta (escolhendo a oferta).
const CURRENT_SUB_STEP: Partial<Record<AuctionProposalStatus, NegotiationSubStep>> = {
  [AuctionProposalStatus.PROCESSING_SIMULATION]: NegotiationSubStep.ANALISE,
  [AuctionProposalStatus.AWAITING_BORROWER_CHOICE]: NegotiationSubStep.PROPOSTA,
};

type StepState = "completed" | "current" | "error" | "future";

const STEP_COLORS: Record<StepState, { bg: string; color: string; line: string }> = {
  completed: { bg: "green.100", color: "green.400", line: "green.100" },
  current: { bg: "green.500", color: "white", line: "gray.300" },
  error: { bg: "red.500", color: "white", line: "gray.300" },
  future: { bg: "gray.100", color: "gray.400", line: "gray.300" },
};

interface StatusStepsProps {
  currentStatus: AuctionProposalStatus;
  negotiationSubStep?: NegotiationSubStep | null;
}

export default function StatusSteps({
  currentStatus,
  negotiationSubStep,
}: StatusStepsProps) {
  const phase = STATUS_NEGOTIATION_PHASE[currentStatus] ?? "before";

  const currentSubStepIndex = (() => {
    if (currentStatus === AuctionProposalStatus.PENDING_FORMALIZATION) {
      return NEGOTIATION_SUB_STEP_ORDER.length - 1;
    }
    if (phase === "during") {
      // Macro-status com substep fixo (PROCESSING→Análise, AWAITING→Proposta)
      // tem prioridade sobre a derivação, que pode oscilar por resíduo/timing.
      const step = CURRENT_SUB_STEP[currentStatus] ?? negotiationSubStep;
      const index = step ? NEGOTIATION_SUB_STEP_ORDER.indexOf(step) : -1;
      return index === -1 ? 0 : index;
    }
    if (phase === "error-during") {
      // Passo de erro: usa o mapeamento fixo quando existe; senão, o substep
      // atual derivado (onde o funil de fato travou).
      const errStep = ERROR_SUB_STEP[currentStatus] ?? negotiationSubStep;
      const idx = errStep ? NEGOTIATION_SUB_STEP_ORDER.indexOf(errStep) : -1;
      return idx === -1 ? 0 : idx;
    }
    return -1;
  })();

  const getStepState = (index: number): StepState => {
    switch (phase) {
      case "before":
        return "future";
      case "after":
        return "completed";
      case "during":
        if (index < currentSubStepIndex) return "completed";
        if (index === currentSubStepIndex) return "current";
        return "future";
      case "error-during":
        if (index < currentSubStepIndex) return "completed";
        if (index === currentSubStepIndex) return "error";
        return "future";
    }
  };

  return (
    <Flex align="center" justify="center">
      {NEGOTIATION_SUB_STEP_ORDER.map((subStep, index) => {
        const state = getStepState(index);
        const colors = STEP_COLORS[state];
        const isLast = index === NEGOTIATION_SUB_STEP_ORDER.length - 1;
        const Ico = SUB_STEP_ICONS[subStep];

        return (
          <Flex key={subStep} align="center">
            <Tooltip
              label={NEGOTIATION_SUB_STEP_LABELS[subStep]}
              placement="top"
              hasArrow
            >
              <Flex
                w={6}
                h={6}
                bg={colors.bg}
                color={colors.color}
                borderRadius="full"
                align="center"
                justify="center"
                cursor="pointer"
                transition="transform 0.15s ease"
                _hover={{ transform: "scale(1.15)" }}
              >
                <Icon as={Ico} boxSize={3} />
              </Flex>
            </Tooltip>
            {!isLast && <Box w={2} h="2px" bg={colors.line} />}
          </Flex>
        );
      })}
    </Flex>
  );
}
